<template>
  <section>
    <FormulateForm name="formClient">
      <b-card-actions
        :show-loading="loading"
        action-collapse
        :title="`${$t('Ficha Cadastral')}${person.id ? ` #${person.id}` : ''}`"
      >
        <customer-form
          ref="clientForm"
          :person="person"
          :is-read-only="isReadOnly"
          :is-edit="isEdit"
          @clean-person-data="cleanPersonTypeData"
          @show-price-table="onShowPriceTableSidebar"
        />

        <!-- Saldo de Recompensas -->
        <div
          v-if="isEdit && person.id"
          class="mt-3"
        >
          <hr>
          <b-row>
            <b-col cols="12">
              <h6 class="mb-2">
                <b-icon
                  icon="gift"
                  variant="success"
                  class="mr-1"
                />
                Programa ecash
              </h6>
              <div
                v-if="loadingRewardBalance"
                class="text-muted"
              >
                <b-spinner small /> Carregando saldo...
              </div>
              <div
                v-else-if="hasRewardBalance"
                class="d-flex align-items-center"
              >
                <b-badge
                  variant="success"
                  pill
                  class="mr-2"
                >
                  {{ formatRewardBalance(rewardBalance) }}
                </b-badge>
                <span class="text-muted small">Saldo disponível em ecash</span>
              </div>
              <div
                v-else
                class="text-muted"
              >
                Nenhum crédito disponível
              </div>
            </b-col>
          </b-row>
        </div>
      </b-card-actions>

      <b-card-actions
        :show-loading="loading"
        action-collapse
        :title="$t('Outras Informações')"
      >
        <!-- each tab has it your own FomulateForm -->
        <b-tabs fill>
          <b-tab :title="$t('Endereços')">
            <e-address-tab
              :is-read-only="isReadOnly"
              person="Customer"
              :addresses="getAddresses"
              @add-address="(formData) => addItem('addresses', formData)"
              @update-address="(formData) => udpateItem('addresses', formData)"
              @remove-address="(id) => removeItem('addresses', id)"
            />
          </b-tab>

          <b-tab :title="$t('Contatos')">
            <e-contact-tab
              :is-read-only="isReadOnly"
              :contacts="getContacts"
              person="Customer"
              @add-contact="(formData) => addItem('contacts', formData)"
              @update-contact="(formData) => udpateItem('contacts', formData)"
              @remove-contact="(id) => removeItem('contacts', id)"
            />
          </b-tab>

          <b-tab :title="$t('Documentos')">
            <files-form
              :files="files"
              :is-read-only="isReadOnly"
              @set-files="setFiles"
              @remove-file="removeFile"
            />
          </b-tab>

          <b-tab :title="$t('Histórico')">
            <history-tab :customer-id="person.id" />
          </b-tab>

          <b-tab
            v-if="isEdit && person.id"
            :title="$t('Recompensas')"
          >
            <reward-extract-tab :customer-id="person.id" />
          </b-tab>
        </b-tabs>
      </b-card-actions>
    </FormulateForm>

    <price-table-sidebar
      ref="pricetable_sidebar"
      @save="() => fetchPriceTables()"
    />

    <e-crud-actions
      :show-save="!isReadOnly"
      :busy="loading"
      @save="saveClient"
      @cancel="onCancel"
    />
  </section>
</template>

<script>
import { BTabs, BTab, BRow, BCol, BBadge, BSpinner, BIcon } from 'bootstrap-vue'
import BCardActions from '@/@core/components/b-card-actions/BCardActions.vue'
import { EAddressTab, EContactTab } from '@/views/components/tabs'
import { mapActions, mapGetters, mapState } from 'vuex'
import { alerts } from '@/mixins'
import ECrudActions from '@/views/components/ECrudActions.vue'
import HistoryTab from './components/client-maintain-tabs/HistoryTab.vue'
import RewardExtractTab from './components/client-maintain-tabs/RewardExtractTab.vue'
import PriceTableSidebar from '../price-table/PriceTableSidebar.vue'
import CustomerForm from './components/CustomerForm.vue'
import FilesForm from './components/client-maintain-tabs/FilesForm.vue'

export default {
  name: 'ClientMaintain',
  components: {
    BCardActions,
    BTabs,
    BTab,
    BRow,
    BCol,
    BBadge,
    BSpinner,
    BIcon,
    HistoryTab,
    RewardExtractTab,
    EAddressTab,
    EContactTab,
    ECrudActions,
    PriceTableSidebar,
    CustomerForm,
    FilesForm,
  },

  mixins: [alerts],

  props: {
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      callbackRouteName: 'client-list',
      customerSaved: null,
    }
  },

  computed: {
    ...mapState('pages/sale/clients/clientMaintain', {
      person: 'person',
      attributes: 'attributes',
      stFiles: 'files',
      stFilesDeleted: 'filesDeleted',
    }),
    ...mapGetters('pages/sale/clients', ['customerClassificationsOptions']),
    ...mapGetters('pages/sale/clients/clientMaintain', ['getAddresses', 'getContacts']),
    ...mapGetters('common/priceTables', ['getComboPriceTables']),
    ...mapState('reward', {
      rewardBalance: 'rewardBalance',
      loadingRewardBalance: 'loadingRewardBalance',
    }),
    ...mapGetters('reward', ['hasRewardBalance', 'formatRewardBalance']),

    isEdit() {
      return !!this.$route.params.id
    },
    files: {
      get() {
        return this.stFiles
      },
      set(val) {
        this.setFiles(val)
      },
    },
  },

  watch: {
    'person.id': {
      immediate: true,
      handler(newId) {
        if (newId && this.isEdit) {
          this.loadRewardBalance()
        }
      },
    },
  },

  mounted() {
    this.fetchPriceTables()
    this.fetchCustomerClassifications()
    this.fetchPartnerships(true)

    if (this.isEdit) {
      this.loadClient()
    }

    if (this.$route.params.callbackRouteName) {
      this.callbackRouteName = this.$route.params.callbackRouteName
    }
  },

  methods: {
    ...mapActions('common/priceTables', ['fetchPriceTables']),
    ...mapActions('pages/sale/clients', ['fetchCustomerClassifications']),
    ...mapActions('pages/partnerships', ['fetchPartnerships']),
    ...mapActions('pages/sale/clients/clientMaintain', [
      'cleanState',
      'cleanPersonTypeData',
      'addAttributeItem',
      'removeAttributeItem',
      'updateAttributeItem',
      'fetchClientById',
      'setFiles',
      'deleteLocalFile',
      'uploadDocuments',
      'deleteDocuments',
    ]),
    ...mapActions('pages/sale/order/orderMaintain', {
      stOrderMaintainFetchCustomerById: 'fetchCustomerById',
    }),
    ...mapActions('reward', ['getCustomerRewardBalance']),

    async loadRewardBalance() {
      if (this.person.id) {
        await this.getCustomerRewardBalance(this.person.id)
      }
    },

    async addItem(itemName, formData) {
      try {
        await this.addAttributeItem({ itemName, formData })
      } catch (err) {
        this.showError({ message: err.message })
      }
    },

    async udpateItem(itemName, formData) {
      try {
        await this.updateAttributeItem({ itemName, formData })
      } catch (err) {
        this.showError({ message: err.message })
      }
    },

    async removeItem(itemName, id) {
      try {
        await this.removeAttributeItem({ itemName, id })
      } catch (err) {
        this.showError({ message: err.message })
      }
    },

    async loadClient() {
      try {
        this.loading = true
        await this.fetchClientById({
          isReadOnly: this.isReadOnly,
          customerId: this.$route.params.id,
        })
      } catch (err) {
        this.showError({ message: err.message })
      } finally {
        this.loading = false
      }
    },

    onShowPriceTableSidebar() {
      this.$refs.pricetable_sidebar.show()
    },

    async saveClient() {
      if (!this.$refs.clientForm.validate()) {
        this.showInvalidDataMessage({ message: '<b>Verifique a ficha cadastral</b>' })
        return
      }

      try {
        this.loading = true

        const { addresses, contacts, bankAccounts, pixes, telephonesApi } = this.attributes

        const { birthdate, gender, stateRegistrationType, telephones } = this.person

        const notInTelephonesForm = tApi => telephones.findIndex(t => t.id === tApi.id) < 0
        const telephonesDeleted = telephonesApi
          .filter(tApi => notInTelephonesForm(tApi))
          .map(tDeleted => ({ ...tDeleted, isDeleted: true }))

        const body = {
          ...this.person,
          person: this.person.personType,
          birthdate,
          gender: gender || null,
          stateRegistrationType: stateRegistrationType || null,
          telephones: [...telephones, ...telephonesDeleted],
          addresses,
          contacts,
          bankAccounts,
          pixes,
        }

        const result = await this.$http({
          url: '/api/customers',
          method: this.isEdit ? 'PUT' : 'POST',
          data: body,
        })

        this.customerSaved = result.data

        this.showSuccess({
          message: this.$t(`${this.isEdit ? 'Atualização' : 'Inclusão'} realizada com sucesso`),
        })

        await this.processDocuments(this.customerSaved)
        await this.executePosSaveActions()
        this.cleanState()
        this.navigateBack()
      } catch (err) {
        this.showError({ error: err })
      } finally {
        this.loading = false
      }
    },

    async processDocuments(data) {
      let hasFileError = false
      try {
        await this.uploadDocuments(data?.id)
      } catch (uploadError) {
        this.showError({
          title: this.$t('Ocorreu um erro ao fazer upload dos anexos'),
          error: uploadError,
        })
        hasFileError = true
      }

      try {
        await this.deleteDocuments(data?.id)
      } catch (deleteDocumentError) {
        this.showError({
          title: this.$t('Ocorreu um erro ao deletar os anexos'),
          error: deleteDocumentError,
        })
        hasFileError = true
      }

      if (!hasFileError && (this.stFiles.some(f => !f.id) || this.stFilesDeleted.length > 0)) {
        this.showSuccess({ message: this.$t('Anexos salvos com sucesso.') })
      }
    },

    async removeFile(file) {
      this.deleteLocalFile(file)
    },

    onCancel() {
      this.cleanState()
      this.navigateBack()
    },

    async updateOrderMaintainCustomer() {
      await this.stOrderMaintainFetchCustomerById({ customerId: this.customerSaved.id })
    },

    async executePosSaveActions() {
      switch (this.callbackRouteName) {
        case 'order-add':
          await this.updateOrderMaintainCustomer()
          break
        default:
          break
      }
    },

    navigateBack() {
      this.$router.push({ name: this.callbackRouteName })
      this.callbackRouteName = 'client-list'
      this.customerSaved = null
    },
  },
}
</script>
